/**
 * useContentModel Hook
 *
 * Loads and manages the V4 content model for the editor.
 * Provides page/section/element accessors and state management.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getEditorContent } from '../../api/editor';
import type {
  EditableSiteContent,
  EditablePage,
  EditableSection,
  EditorPageInfo,
  EditorWebsiteInfo,
  EditorVersionInfo,
} from '../types';

// =============================================================================
// TYPES
// =============================================================================

export interface ContentModelState {
  /** Whether the content model is loading */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Whether there's any content to edit */
  hasContent: boolean;
  /** Website metadata */
  website: EditorWebsiteInfo | null;
  /** Current version info */
  version: EditorVersionInfo | null;
  /** The structured content model */
  editableContent: EditableSiteContent | null;
  /** Raw HTML sections (for serving unedited content) */
  rawSections: Record<string, string>;
  /** Ordered list of pages */
  pageList: EditorPageInfo[];
}

export interface ContentModelActions {
  /** Reload content from API */
  reload: () => Promise<void>;
  /** Get a specific page's content */
  getPage: (slug: string) => EditablePage | null;
  /** Get a specific section by ID */
  getSection: (sectionId: string) => EditableSection | null;
  /** Get all sections for a page */
  getSections: (pageSlug: string) => EditableSection[];
  /** Get raw HTML for a section */
  getRawSectionHtml: (sectionKey: string) => string | null;
  /** Optimistically update an element's value in local state (no API call) */
  updateElement: (elementId: string, newValue: string) => void;
}

// =============================================================================
// HOOK
// =============================================================================

export interface ContentModelOptions {
  /** Custom content fetcher (for portal/customer-scoped API) */
  fetchContent?: (websiteId: string) => Promise<import('../types').EditorContentResponse>;
}

export function useContentModel(
  websiteId: string | undefined,
  options?: ContentModelOptions
): ContentModelState & ContentModelActions {
  const [state, setState] = useState<ContentModelState>({
    isLoading: true,
    error: null,
    hasContent: false,
    website: null,
    version: null,
    editableContent: null,
    rawSections: {},
    pageList: [],
  });

  // Load content model from API
  const loadContent = useCallback(async () => {
    if (!websiteId) return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const fetcher = options?.fetchContent || getEditorContent;
      const response = await fetcher(websiteId);

      if (!response.success) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: response.message || 'Failed to load content',
          hasContent: false,
        }));
        return;
      }

      setState({
        isLoading: false,
        error: null,
        hasContent: response.hasContent,
        website: response.website || null,
        version: response.version || null,
        editableContent: response.editableContent || null,
        rawSections: response.rawSections || {},
        pageList: response.pageList || [],
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load content',
      }));
    }
  }, [websiteId]);

  // Load on mount and when websiteId changes
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Page accessor
  const getPage = useCallback(
    (slug: string): EditablePage | null => {
      if (!state.editableContent) return null;
      return state.editableContent.pages[slug] || null;
    },
    [state.editableContent]
  );

  // Section accessor
  const getSection = useCallback(
    (sectionId: string): EditableSection | null => {
      if (!state.editableContent) return null;

      for (const page of Object.values(state.editableContent.pages)) {
        const section = page.sections.find((s) => s.id === sectionId);
        if (section) return section;
      }
      return null;
    },
    [state.editableContent]
  );

  // Get all sections for a page
  const getSections = useCallback(
    (pageSlug: string): EditableSection[] => {
      const page = getPage(pageSlug);
      return page?.sections || [];
    },
    [getPage]
  );

  // Raw HTML accessor
  const getRawSectionHtml = useCallback(
    (sectionKey: string): string | null => {
      return state.rawSections[sectionKey] || null;
    },
    [state.rawSections]
  );

  // Optimistic element update — patches local state without an API call
  const updateElement = useCallback(
    (elementId: string, newValue: string) => {
      setState((prev) => {
        if (!prev.editableContent) return prev;

        // Deep-clone and patch the element across all pages/sections
        const updatedPages: Record<string, EditablePage> = {};
        let found = false;

        for (const [slug, page] of Object.entries(prev.editableContent.pages)) {
          const updatedSections = page.sections.map((section) => {
            const updatedElements = section.elements.map((el) => {
              if (el.id === elementId) {
                found = true;
                return { ...el, value: newValue };
              }
              return el;
            });
            return found ? { ...section, elements: updatedElements } : section;
          });
          updatedPages[slug] = { ...page, sections: updatedSections };
        }

        if (!found) return prev;

        return {
          ...prev,
          editableContent: {
            ...prev.editableContent,
            pages: updatedPages,
          },
        };
      });
    },
    []
  );

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(
    () => ({
      ...state,
      reload: loadContent,
      getPage,
      getSection,
      getSections,
      getRawSectionHtml,
      updateElement,
    }),
    [state, loadContent, getPage, getSection, getSections, getRawSectionHtml, updateElement]
  );
}
